import React, { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { BtnWrap } from '@components/wrapper/Modal/ModalStyle';
import { masterSet } from '@masterSetting/masterSet/masterSetting';
import { IhomepageSetting } from '@typings/setting/homepageSetting.interface';
import { useNavigate } from 'react-router-dom';
import { IPhotoCard } from '@typings/photocard/photocard.interface';
import { CollectionPath } from '@typings/collections.enum';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import useToggle from '@hooks/useToggle';
import InventoryList from '@pages/PhotoCard/PhotoCardDetail/statusContents/inventory/InventoryList';
import { layoutTypeState } from '@atom/setting/homepageSet';
import {
  CHAR_PANEL_TYPE,
  CHAR_PANEL_TYPE_VALUE,
} from '@typings/menu/charPanelType';
import { petOpenState } from '@atom/setting/jukebox';
import styled from 'styled-components';
import Icon from '@components/dataDisplay/Icons/Icon';
import icons from '@components/dataDisplay/Icons/Icons';
import SideModalWrapper from '@components/wrapper/SideModalWrapper';
import MiniQuest from '@pages/Quest/MiniQuest/MiniQuest';
import { BOARD_TYPE, ImasterSet } from '@typings/boardType';

// 퀘스트 보드가 활성화되어 있는지 확인
function isBoardIncluded(
  masterSetData: ImasterSet,
  targetTypeName: string,
): boolean {
  return masterSetData.boardSet.some(
    (board) => board.typeName === targetTypeName,
  );
}

const RouteBtnWrap = styled.button<{ $iconColor: string }>`
  padding: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  color: ${({ $iconColor }) => $iconColor};
  border-radius: 8px;
  z-index: 1;

  span {
    font-size: 11px;
    transition: text-shadow 0.25s ease, color 0.25s ease;
  }

  svg {
    transition: filter 0.25s ease;
  }

  &:hover {
    cursor: var(--hoverCursorURL), pointer;

    span {
      color: ${({ $iconColor }) => $iconColor};
      text-shadow: 0 0 6px ${({ $iconColor }) => $iconColor};
    }

    svg {
      filter: drop-shadow(0 0 4px ${({ $iconColor }) => $iconColor});
    }
  }
`;

// 퀘스트 사이드 패널 스타일
const QuestSidePanel = styled.div`
  width: 420px;
  max-width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: var(--backgroundColor);
`;

const QuestSidePanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid var(--lineColor);
`;

const QuestSidePanelTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  color: var(--textColor);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const QuestSidePanelCloseBtn = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--textColor);
  transition: all 0.2s ease;

  &:hover {
    background: var(--inkColor);
    cursor: var(--hoverCursorURL), pointer;
  }
`;

const QuestSidePanelContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
`;

type Props = {
  homeSet: IhomepageSetting;
  data: IPhotoCard | undefined;
  isVisible: boolean;
  direction?: CHAR_PANEL_TYPE_VALUE;
  /** 아이콘 및 텍스트 색상. 기본값: var(--textColor) */
  iconColor?: string;
};
function ActionButtons({
  homeSet,
  data,
  isVisible,
  direction,
  iconColor = 'var(--textColor)',
}: Props) {
  const layoutType = useRecoilValue(layoutTypeState);
  const dir =
    direction || homeSet.cummuMode?.panelDir || CHAR_PANEL_TYPE.TOPRIGHT;

  const navigate = useNavigate();
  const [inventoryVisible, toggleInventoryVisible] = useToggle(false);
  const [questPanelVisible, setQuestPanelVisible] = useState(false);
  const setPetDraggableOpen = useSetRecoilState(petOpenState);

  const gotoMyroom = useCallback(() => {
    if (!data || !data.linkStickerboardId)
      return alert('연결된 마이룸이 존재하지 않습니다.');

    window.open(
      `/${CollectionPath.STICKERBOARD}/${data.linkStickerboardId}?mute=true&photocardowner=${data?.owner}`,
      '_blank',
      'width=1080 height=760',
    );
  }, [data]);

  const gotoProfile = useCallback(() => {
    if (!data || !data.id) {
      return alert('대표 캐릭터를 먼저 설정해주세요.');
    }
    navigate(`/${CollectionPath.PHOTOCARD}/${data.id}`);
  }, [data, navigate]);

  const gotoPetView = useCallback(() => {
    setPetDraggableOpen((prev) => !prev);
  }, []);

  const toggleQuestPanel = useCallback(() => {
    setQuestPanelVisible((prev) => !prev);
  }, []);

  return (
    <>
      <BtnWrap>
        {(homeSet.cummuMode?.panelSections?.profile ?? true) && (
          <RouteBtn
            icon="Person14"
            label="프로필"
            onClick={gotoProfile}
            iconColor={iconColor}
          />
        )}

        {masterSet.petSystem &&
          (homeSet.cummuMode?.panelSections?.pet ?? true) && (
            <RouteBtn
              icon="Pet14"
              label="펫"
              onClick={gotoPetView}
              iconColor={iconColor}
            />
          )}

        {(homeSet.cummuMode?.panelSections?.myHome ?? true) && (
          <RouteBtn
            icon="Home14"
            label="마이룸"
            onClick={gotoMyroom}
            iconColor={iconColor}
          />
        )}

        {(homeSet.cummuMode?.panelSections?.inventory ?? true) && (
          <RouteBtn
            icon="Inventory14"
            label="인벤토리"
            onClick={toggleInventoryVisible}
            iconColor={iconColor}
          />
        )}

        {(homeSet.cummuMode?.panelSections?.quest ?? true) && (
          <RouteBtn
            icon="Info14"
            label="퀘스트"
            onClick={toggleQuestPanel}
            iconColor={iconColor}
          />
        )}
      </BtnWrap>

      {createPortal(
        <SideModalWrapper
          show={inventoryVisible}
          direction="right"
          outsideClick={toggleInventoryVisible}
          zIndex={999}
          maxWidth={420}
          noOverlay
        >
          <QuestSidePanel>
            <QuestSidePanelHeader>
              <QuestSidePanelTitle>
                <Icon name="Inventory14" color="var(--textColor)" />
                인벤토리
              </QuestSidePanelTitle>
              <QuestSidePanelCloseBtn onClick={toggleInventoryVisible}>
                <Icon name="X14" color="var(--textColor)" />
              </QuestSidePanelCloseBtn>
            </QuestSidePanelHeader>
            <QuestSidePanelContent>
              <InventoryList
                photocardData={data}
                collectionPath={data?.path || CollectionPath.PHOTOCARD}
                photocardID={data?.id || ''}
              />
            </QuestSidePanelContent>
          </QuestSidePanel>
        </SideModalWrapper>,
        document.body,
      )}

      {createPortal(
        <SideModalWrapper
          show={questPanelVisible}
          direction="left"
          outsideClick={() => setQuestPanelVisible(false)}
          zIndex={999}
          maxWidth={420}
          noOverlay
        >
          <QuestSidePanel>
            <QuestSidePanelHeader>
              <QuestSidePanelTitle>
                <Icon name="Info14" color="var(--textColor)" />
                퀘스트
              </QuestSidePanelTitle>
              <QuestSidePanelCloseBtn
                onClick={() => setQuestPanelVisible(false)}
              >
                <Icon name="X14" color="var(--textColor)" />
              </QuestSidePanelCloseBtn>
            </QuestSidePanelHeader>
            <QuestSidePanelContent>
              <MiniQuest />
            </QuestSidePanelContent>
          </QuestSidePanel>
        </SideModalWrapper>,
        document.body,
      )}
    </>
  );
}

function RouteBtn({
  icon,
  label,
  onClick,
  iconColor,
}: {
  icon: keyof typeof icons;
  label?: string;
  onClick: () => void;
  iconColor: string;
}) {
  return (
    <RouteBtnWrap
      $iconColor={iconColor}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
    >
      <Icon name={icon} color={iconColor} />
      {label && <span>{label}</span>}
    </RouteBtnWrap>
  );
}

export default ActionButtons;
